import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { TranslatorContext } from '@jutro/locale';
import { Avatar, Icon } from '@jutro/components';
import { ImageComponent } from 'gw-components-platform-react';
import { ServiceManager } from '@jutro/legacy/services';
import { PhoneUtil } from '@xengage/gw-portals-viewmodel-js';
import { ContactService } from 'gw-capability-gateway';
import { withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import metadata from './AssociatedContactsPage.metadata.json5';
import tabStyles from '../Contacts.module.scss';
import messages from '../Contacts.messages';
import gatewayMessages from '../../gateway.messages';

const PRIMARY_PHONE_TYPE_MAP = {
    home: 'homeNumber',
    mobile: 'cellNumber',
    work: 'workNumber'
};

class AssociatedContactsPage extends Component {
    static contextType = TranslatorContext;

    state = {
        associatedContactsTableData: {},
        selectedTab: 'all'
    };

    static propTypes = {
        accountName: PropTypes.string.isRequired,
        accountNumber: PropTypes.string.isRequired,
        relatedTo: PropTypes.string.isRequired,
        authHeader: PropTypes.shape({}).isRequired,
        history: PropTypes.shape({
            push: PropTypes.func
        }).isRequired
    };

    getAssociatedContacts = (selectedTab, paginationParams) => {
        const { accountNumber, authHeader } = this.props;
        let getContactsPromise;

        if (selectedTab !== 'commoncontacts') {
            const relationships = selectedTab ? [selectedTab] : [];

            getContactsPromise = ContactService.getRelatedAccountContacts(
                accountNumber,
                authHeader,
                relationships,
                paginationParams.fromPage,
                paginationParams.pageSize
            );
        } else {
            getContactsPromise = ContactService.getCommonAccountContacts(
                accountNumber,
                authHeader,
                paginationParams.fromPage,
                paginationParams.pageSize
            );
        }

        return getContactsPromise.then((response) => {
            this.setState({
                associatedContactsTableData: response
            });

            const data = this.getResponseData(response);

            return {
                rows: data,
                numberOfRows: response.maxNumberOfResults
            };
        });
    };

    getResponseData = (data) => {
        const { accountContacts } = data;

        if (accountContacts) {
            const contactsInfo = accountContacts.map((contact) => ({
                    contact: contact.displayName,
                    account: contact.relatedAccountName,
                    involvedWith: contact.associatedPoliciesCount,
                    active: contact.active
                }));

            return contactsInfo;
        }

        return [];
    };

    redirectToContactDetails = (index) => {
        const { accountNumber, relatedTo, history } = this.props;
        const { associatedContactsTableData } = this.state;
        const { accountContacts } = associatedContactsTableData;

        history.push({
            pathname: `/${relatedTo}/${accountNumber}/contactDetails`,
            state: {
                contact: 'associatedContacts',
                accountOrPolicyNumber: accountNumber,
                relatedTo,
                contactId: accountContacts[index].accountContactPublicID
            }
        });
    };

    renderContact = (item, index, { path: property }) => {
        const { associatedContactsTableData } = this.state;
        const translator = this.context;

        if (index < associatedContactsTableData.accountContacts.length) {
            const contact = associatedContactsTableData.accountContacts[index];
            let primaryPhoneNumber = '';

            const contactAvatarClass = cx(tabStyles.gwContactAvatar, {
                [tabStyles.gwActiveContactAvatar]: item.active,
                [tabStyles.gwInactiveContactAvatar]: !item.active
            });

            if (contact.primaryPhoneType) {
                const phoneNumber = contact[PRIMARY_PHONE_TYPE_MAP[contact.primaryPhoneType]];
                const localeService = ServiceManager.getService('locale-service');
                const phoneUtil = PhoneUtil();

                primaryPhoneNumber = phoneUtil.prettyPrint(
                    phoneNumber,
                    localeService.getDefaultCountryCode()
                );
            }

            const phoneIconClass = cx(tabStyles.gwContactPhoneIcon, 'fa-flip-horizontal');

            const primaryPhoneNumberContainer = primaryPhoneNumber && (
                <div className={tabStyles.gwContactPhoneNumber}>
                    <Icon
                        className={phoneIconClass}
                        icon="mi-phone"
                        onClick={() => {
                            this.redirectToContactDetails(index);
                        }}
                    />
                    {primaryPhoneNumber}
                </div>
            );

            const emailAddressContainer = contact.emailAddress1 && (
                <div className={tabStyles.gwContactEmail}>
                    <Icon
                        className={tabStyles.gwContactEmailIcon}
                        icon="mi-email"
                        onClick={() => {
                            this.redirectToContactDetails(index);
                        }}
                    />
                    {contact.emailAddress1}
                </div>
            );

            const commonContact = contact.isCommonContact && (
                <div>
                    <ImageComponent
                        className={tabStyles.gwContactCommonImage}
                        src="/branding/venn-intersection.svg"
                        alt="common-contact"
                        onClick={() => {
                            this.redirectToContactDetails(index);
                        }}
                    />
                    <div className={tabStyles.gwContactCommonText}>
                        {translator(messages.commonContact)}
                    </div>
                </div>
            );

            return (
                <div className={tabStyles.gwContactsTableWrapper}>
                    <div className={tabStyles.gwContactAvatarContainer}>
                        <Avatar
                            className={contactAvatarClass}
                            username={item[property]}
                            onClick={() => {
                                this.redirectToContactDetails(index);
                            }}
                        />
                        {commonContact}
                    </div>
                    <div className={tabStyles.gwContactNameContainer}>
                        <div className={tabStyles.gwContactName}>{item[property]}</div>
                        <div className={tabStyles.gwContactPhoneNumberWrapper}>
                            {primaryPhoneNumberContainer}
                            {emailAddressContainer}
                        </div>
                    </div>
                </div>
            );
        }

        return <div />;
    };


    onAssociatedContactsTabClick = (tabId) => {
        this.setState({
            selectedTab: tabId
        });
    };

    renderPoliciesInvolved = (item, index, { path: property }) => {
        const policiesCount = item[property];
        const isActive = item.active;

        const policiesInvolved = isActive
            ? (
                <TranslatorContext.Consumer>
                    {(translator) => {
                        const activePolicies = policiesCount > 1
                            ? translator(messages.activePolicies)
                            : translator(messages.activePolicy);

                        return (
                            <React.Fragment>
                                <span className={tabStyles.gwInvolvedPolicies}>
                                    {policiesCount}
                                </span>
                                {activePolicies}
                            </React.Fragment>
                        );
                    }}
                </TranslatorContext.Consumer>
            )
            : (
                <TranslatorContext.Consumer>
                    {(translator) => (
                            translator(gatewayMessages.inactive)
                        )}
                </TranslatorContext.Consumer>
            );

        return (
            <div className={tabStyles.gwInvolvedPoliciesContainer}>
                <span>{policiesInvolved}</span>
                <Icon
                    className={tabStyles.gwInvolvedPoliciesArrow}
                    icon="mi-keyboard-arrow-right"
                    onClick={() => {
                        this.redirectToContactDetails(index);
                    }}
                />
            </div>
        );
    };

    loadData = ({ pageSize, page }) => {
        const { selectedTab } = this.state;
        const tab = selectedTab === 'all' ? null : selectedTab.toLowerCase();
        const paginationParams = {
            fromPage: page,
            pageSize
        };

        return this.getAssociatedContacts(tab, paginationParams);
    };

    render() {
        const { selectedTab } = this.state;
        const { accountName, accountNumber } = this.props;
        const translator = this.context;

        const overrideProps = {
            associatedContactsDataTable: {
                key: selectedTab,
                onFetchData: this.loadData
            },
            associatedTabSetContainer: {
                defaultActiveTab: selectedTab,
                activeTab: selectedTab
            },
            contactsRelatedByDescription: {
                content: translator(messages[`${selectedTab}RelationshipDescription`], {
                    accountName,
                    accountNumber
                })
            }
        };

        const resolvers = {
            resolveClassNameMap: tabStyles,
            resolveCallbackMap: {
                renderContact: this.renderContact,
                renderAccount: this.renderAccount,
                renderPoliciesInvolved: this.renderPoliciesInvolved,
                onAssociatedContactsTabClick: this.onAssociatedContactsTabClick,
                onRowClick: (data, index) => {
                    this.redirectToContactDetails(index);
                }
            }
        };

        return (
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
            />
        );
    }
}

export default withRouter(withAuthenticationContext(AssociatedContactsPage));
